import React from "react";
import { AlertCircle, X } from "lucide-react";

// Phase 3L.1 Pass B — in-app confirm modal. Drop-in replacement for
// `window.confirm` so the confirmation surface is keyboard-accessible,
// brand-consistent, and Playwright-testable without juggling dialog
// handlers.
//
// Usage:
//   <ConfirmModal
//     open={Boolean(toArchive)}
//     title="Archive link profile?"
//     message={`"${toArchive?.name}" will be hidden but can be restored later.`}
//     confirmLabel="Archive"
//     onCancel={() => setToArchive(null)}
//     onConfirm={() => doArchive(toArchive)}
//   />
export default function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "warning", // "warning" | "danger" | "info"
  onCancel,
  onConfirm,
  busy = false,
  testIdPrefix = "confirm",
}) {
  if (!open) return null;
  const accent = tone === "danger"
    ? "bg-red-500 hover:bg-red-600 text-white"
    : tone === "info"
    ? "bg-slate-700 hover:bg-slate-800 text-white"
    : "bg-orange-500 hover:bg-orange-600 text-white";
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6"
      data-testid={`${testIdPrefix}-modal`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testIdPrefix}-title`}
    >
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onCancel}/>
      <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-xl">
        <div className="px-5 py-4 flex items-start gap-3">
          <div className={"w-9 h-9 rounded-full grid place-items-center shrink-0 " + (tone === "danger" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600")}>
            <AlertCircle className="w-5 h-5"/>
          </div>
          <div className="min-w-0 flex-1">
            <h2 id={`${testIdPrefix}-title`} className="text-base font-semibold text-slate-900">{title}</h2>
            {message && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{message}</p>}
          </div>
          <button
            type="button"
            onClick={busy ? undefined : onCancel}
            aria-label="Close"
            data-testid={`${testIdPrefix}-close`}
            className="text-slate-400 hover:text-slate-700 shrink-0"
          >
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="px-5 pb-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testIdPrefix}-cancel`}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testIdPrefix}-accept`}
            className={"px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60 " + accent}
          >
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
